import React, { useEffect, useState } from 'react';
import { Row, Col, Button, message, Space, Popconfirm } from 'antd';
import ListCard from 'Molecules/ListCard';
import HeadingChip from 'Molecules/HeadingChip';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getRegionList } from '../ducks/actions'
import { removeRegion, bulkRemoveRegions } from '../ducks/services'
import ActionButton from 'Molecules/ActionButton';
import Search from './components/Search'
import AllRoles from '../../../../routing/config/AllRoles';
import { allowed } from '../../../../routing/config/utils';
import { getListQueryItems } from 'Modules/Application/ducks/actions';
import { useTranslate } from 'Translate';

export default (props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const regions = useSelector((state) => state.systems.regions);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchVal, setSearchVal] = useState(null);
  const allowedEdit = allowed([AllRoles.SYSTEM.REGION], 'write');
  const allowedDelete = allowed([AllRoles.SYSTEM.REGION], 'delete');
  const globalListQuery = useSelector((state) => state.global.listQueryItems);
  const i18n = useTranslate();
  const { t } = i18n;

  const colName = [
    {
      title: t("General.code"),
      dataIndex: 'code',
      key: 'code',
      className: 'enable-click',
      sorter: true
    },
    {
      title: t("General.desc"),
      dataIndex: 'desc',
      key: 'desc',
      className: 'enable-click',
      sorter: true
    },
    {
      title: t("General.status"),
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      render: (text, record) =>
        <div className='flex align-items-center'>
          <span className={text === "Active" ? "c-success" : "c-danger"}>
            {t(`General.${text?.toLowerCase()}`)}
          </span>
          {
            allowedEdit &&
            <Button type="link" onClick={() => onAction(record.id, text)}>
              {text === "Active" ? `(${t("General.deactivate")})` : `(${t("General.activate")})`}
            </Button>
          }
        </div>
    },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      align: 'center',
      render: (text, record) =>
        <ActionButton
          title="region"
          btnAction1={() => navigate(`/region/${record.id}`)}
          recordId={record.id}
          onRemove={allowedDelete && onRemove}
          setLoading={props.setLoading}
          loading={props.loading}
        />
    }
  ]

  const addNew = () => navigate('/region/add');

  const btnList = [
    {
      title: t("System.confirm_remove_select_region"),
      text: t("General.bulk_remove"),
      classes: 'red-btn text-white attendance-success',
      permit: true,
      action: () => onBulkRemove(),
    },
    {
      text: `+ ${t("System.new_region")}`,
      action: () => addNew(),
    },
  ];

  useEffect(() => {
    if(!globalListQuery.regionListQuery) {
      dispatch(getRegionList(1, limit, '', '', null, props.setLoading));
      dispatch(getListQueryItems('regionListQuery', 1, limit, '', '', null, props.setLoading));
    } else {
      const {page, limit, sortOrder, sortColKey, searchValue} = globalListQuery.regionListQuery
      dispatch(getRegionList(page, limit, sortOrder, sortColKey, searchValue, props.setLoading));
      setPage(page);
      setLimit(limit);
      setSearchVal(searchValue);
    }
  }, []);

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const onAction = async (regionId, status) => {
    props.setLoading(true);

    const payload = {
      region_id: regionId,
      status_event: status === 'Active' ? 'deactivate' : 'activate'
    }

    await removeRegion(payload).then(res => {
      props.setLoading(false);
      if (res.data.code === 200) {
        message.success(t("System.regions_status_update_success"));
        setTimeout(() => dispatch(getRegionList(page, limit, '', '', searchVal, props.setLoading)), 500);
        dispatch(getListQueryItems('regionListQuery', page, limit, '', '', searchVal, props.setLoading))
      }
    }).catch(e => {
      props.setLoading(false);
      message.error(e.message ?? t("General.something_went_wrong"));
    })
  }

  const onRemove = async (id) => {
    props.setLoading(true);

    const payload = {
      region_id: id,
      status_event: 'remove'
    }

    await removeRegion(payload).then(res => {
      props.setLoading(false);
      if (res.data.code === 200) {
        message.success(t("System.region_remove_success"));
        setTimeout(() => dispatch(getRegionList(page, limit, '', '', searchVal, props.setLoading)), 500);
        dispatch(getListQueryItems('regionListQuery', page, limit, '', '', searchVal, props.setLoading))
      }
    }).catch(e => {
      props.setLoading(false);
      message.error(e.message ?? t("General.something_went_wrong"));
    })
  }

  const onBulkRemove = async () => {
    props.setLoading(true);

    const payload = {
      regions: JSON.stringify(selectedRowKeys)
    }

    await bulkRemoveRegions(payload).then(res => {
      props.setLoading(false);
      if (res.data.code === 200) {
        rowSelection.onChange([]);
        message.success(t("System.regions_remove_success"));
        setPage(1);
        setSearchVal(null);
        setTimeout(() => dispatch(getRegionList(1, limit, '', '', null, props.setLoading)), 500);
        dispatch(getListQueryItems('regionListQuery', 1, limit, '', '', null, props.setLoading))
      }
    }).catch(e => {
      props.setLoading(false);
      message.error(e.message ?? t("General.something_went_wrong"));
    })
  }

  const onTableChange = (pagination, filters, sorter) => {
    setPage(pagination.current);
    setLimit(pagination.pageSize);
    if (sorter.order) {
      dispatch(getRegionList(pagination.current, pagination.pageSize, sorter.order, sorter.columnKey, searchVal, props.setLoading));
      dispatch(getListQueryItems('regionListQuery', pagination.current, pagination.pageSize, sorter.order, sorter.columnKey, searchVal, props.setLoading))
    } else {
      dispatch(getRegionList(pagination.current, pagination.pageSize, '', '', searchVal, props.setLoading));
      dispatch(getListQueryItems('regionListQuery', pagination.current, pagination.pageSize, '', '', searchVal, props.setLoading))
    }
  }

  const onSearch = (search) => {
    setPage(1);
    if (search) {
      let searching = {};
      searching = {
        name: search.name,
        code: search.code
      };
      setSearchVal(searching);
      dispatch(getRegionList(1, limit, '', '', searching, props.setLoading));
      dispatch(getListQueryItems('regionListQuery', 1, limit, '', '', searching, props.setLoading))
    } else {
      setSearchVal(null);
      dispatch(getRegionList(1, limit, '', '', null, props.setLoading));
      dispatch(getListQueryItems('regionListQuery', 1, limit, '', '', null, props.setLoading))
    }
  };

  const onClickRow = (record) => {
    return {
      onClick: (e) => {
        if (e.target.closest(".enable-click"))
          navigate(`/region/${record.id}`)
      },
    };
  };

  return (
    <>
      <Row gutter={[10, 10]}>
        <Col span={24}>
          <HeadingChip title={t("General.regions")} btnList={allowedEdit ? btnList : null} btnHidden={selectedRowKeys.length <= 0} />
        </Col>
        <Col span={24} className="clickRow">
          <ListCard
            onRow={onClickRow}
            Search={Search}
            onSearch={onSearch}
            searchVal={globalListQuery.regionListQuery?.searchValue}
            ListData={regions?.list?.map(x => ({ ...x, key: x.id }))}
            ListCol={colName}
            rowSelection={allowedEdit && rowSelection}
            onChange={onTableChange}
            pagination={{
              total: regions?.total_count,
              current: page,
              pageSize: limit
            }}
          />
        </Col>
      </Row>
    </>
  )
}